import { useCallback, useEffect, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useArtistEventChatListState(context) {
  const {
    API_URL,
    ARTIST_SEARCH_API,
    EVENTS_PAGE_LIMIT,
    applyEventTargetSearchDraft,
    applySearchResultEvents,
    buildToolbarFilterChips,
    clearEventTargetSearch,
    composeArtistEventRowWithComposite,
    countActiveEventSearchFilters,
    displayedIdsRef,
    eventListForceFallbackExhaustedRef,
    eventListLoadMorePendingRef,
    eventListNode,
    eventUnreadMapArtist,
    events,
    fetchEvents,
    fetchJsonSafe,
    formatSearchChipDate,
    getCompositesBatched,
    hasMoreEvents,
    isBootLoading,
    isLoadingEvents,
    isSearchLoadingMore,
    isSearchMode,
    isSearching,
    joinEventRooms,
    joinedRoomsRef,
    loadEventSearchRowsWithUnread,
    loadMoreEventList,
    loadMoreNode,
    mapRowsByEventUuid,
    messageSearchQuery,
    observeEventListLoadMoreScroll,
    observeEventListLoadMoreSentinel,
    openEventTargetSearchModal,
    orderFrozen,
    prevUnreadMapRef,
    refreshTopTimerRef,
    resolveEventSearchCriteria,
    scheduleEventListRefreshTop,
    searchDraft,
    searchEndDate,
    searchHasMore,
    searchNextOffset,
    searchQuery,
    searchSnapshotToken,
    searchStartDate,
    setEvents,
    setEventsOffset,
    setHasMoreEvents,
    setIsSearchLoadingMore,
    setIsSearchMode,
    setIsSearching,
    setMessageSearchQuery,
    setSearchDraft,
    setSearchEndDate,
    setSearchError,
    setSearchHasMore,
    setSearchNextOffset,
    setSearchQuery,
    setSearchSnapshotToken,
    setSearchStartDate,
    setShowSearchModal,
    shouldLoadMoreEventList,
    socket,
    toastError,
    triggerEventListLoadMoreIfNeeded,
  } = context;
    const activeSearchFilterCount = useMemo(
      () =>
        countActiveEventSearchFilters({
          eventQuery: searchQuery,
          messageQuery: messageSearchQuery,
          startDate: searchStartDate,
          endDate: searchEndDate,
        }),
      [messageSearchQuery, searchEndDate, searchQuery, searchStartDate]
    );
    const searchSummaryLabel =
      activeSearchFilterCount > 0
        ? `絞り込み中: ${activeSearchFilterCount}件`
        : '';

    const openTargetSearchModal = useCallback(() => {
      openEventTargetSearchModal({
        setErrorMessage: setSearchError,
        setSearchDraft,
        setShowSearchModal,
        currentSearch: {
          eventQuery: searchQuery,
          messageQuery: messageSearchQuery,
          startDate: searchStartDate,
          endDate: searchEndDate,
        },
      });
    }, [messageSearchQuery, searchEndDate, searchQuery, searchStartDate]);

    const clearSearch = useCallback(async () => {
      setSearchHasMore(false);
      setSearchNextOffset(0);
      setSearchSnapshotToken('');
      setIsSearchLoadingMore(false);
      await clearEventTargetSearch({
        setErrorMessage: setSearchError,
        setIsSearchMode,
        setSearchQuery,
        setMessageSearchQuery,
        setSearchStartDate,
        setSearchEndDate,
        setHasMoreEvents,
        setEventsOffset,
        reloadDefaultList: () => fetchEvents('reset'),
      });
    }, [fetchEvents]);

    const performSearch = useCallback(async (override = null, { append = false } = {}) => {
      const criteria = resolveEventSearchCriteria({
        override,
        eventQuery: searchQuery,
        messageQuery: messageSearchQuery,
        startDate: searchStartDate,
        endDate: searchEndDate,
      });
      if (!criteria.hasAny) {
        if (isSearchMode) {
          await clearSearch();
        } else {
          setSearchError('');
          setIsSearchMode(false);
        }
        return true;
      }
      try {
        setSearchError('');
        if (!append) setIsSearching(true);
        setIsSearchMode(true);

        const {
          rawEvents,
          unreadMap: unreadMapResp,
          hasMore: nextHasMore,
          nextOffset,
          snapshotToken,
        } = await loadEventSearchRowsWithUnread({
          searchUrl: ARTIST_SEARCH_API({
            q: criteria.q,
            message: criteria.message,
            startDate: criteria.startDate,
            endDate: criteria.endDate,
            offset: append ? searchNextOffset : 0,
            snapshotToken: append ? searchSnapshotToken : '',
          }),
          fetchJsonSafe,
          searchErrorLabel: '検索に失敗しました。',
          apiUrl: API_URL,
          role: 'artist',
          unreadLabel: '',
        });

        const ids = rawEvents.map(evt => evt.event_uuid).filter(Boolean);
        const composites = await getCompositesBatched(ids, 4);
        const compMap = mapRowsByEventUuid(composites);

        const patched = rawEvents.map((evt) =>
          composeArtistEventRowWithComposite({
            eventRow: evt,
            compositeRow: compMap.get(String(evt?.event_uuid || '').trim()),
            unreadCount: unreadMapResp[evt.event_uuid] || 0,
            preferEventPerformanceType: true,
          })
        );

        const rowKeyOf = (row) =>
          row?.search_hit_message_id
            ? `${row.event_uuid}-${row.search_hit_message_id}`
            : String(row?.event_uuid || '');

        if (append) {
          setEvents((prev) => {
            const seen = new Set(prev.map(rowKeyOf));
            const additions = patched.filter((row) => !seen.has(rowKeyOf(row)));
            return [...prev, ...additions];
          });
        } else {
          applySearchResultEvents({
            resultEvents: patched,
            setEvents,
            setHasMoreEvents,
            setEventsOffset,
          });
        }
        setSearchHasMore(nextHasMore);
        setSearchNextOffset(nextOffset);
        if (snapshotToken) setSearchSnapshotToken(snapshotToken);

        joinEventRooms({ socket, joinedRoomsRef, events: patched });
        return true;
      } catch (e) {
        const message = e?.message || '検索に失敗しました。';
        setSearchError(message);
        toastError(message);
        return false;
      } finally {
        if (!append) setIsSearching(false);
      }
    }, [clearSearch, getCompositesBatched, isSearchMode, messageSearchQuery, searchEndDate, searchNextOffset, searchQuery, searchSnapshotToken, searchStartDate, toastError]);

    const loadMoreSearchResults = useCallback(async () => {
      if (!isSearchMode || !searchHasMore || isSearchLoadingMore || isSearching) return;
      setIsSearchLoadingMore(true);
      try {
        await performSearch(null, { append: true });
      } finally {
        setIsSearchLoadingMore(false);
      }
    }, [isSearchMode, searchHasMore, isSearchLoadingMore, isSearching, performSearch]);

    const applyToolbarSearchFilters = useCallback(async (override = {}) => {
      const criteria = resolveEventSearchCriteria({
        override,
        eventQuery: searchQuery,
        messageQuery: messageSearchQuery,
        startDate: searchStartDate,
        endDate: searchEndDate,
      });
      setSearchError('');
      setSearchQuery(criteria.q);
      setMessageSearchQuery(criteria.message);
      setSearchStartDate(criteria.startDate);
      setSearchEndDate(criteria.endDate);
      if (!criteria.hasAny) {
        setIsSearchMode(false);
        setHasMoreEvents(true);
        setEventsOffset(0);
        if (isSearchMode) {
          await fetchEvents('reset');
        }
        return true;
      }
      return performSearch({
        eventQuery: criteria.q,
        messageQuery: criteria.message,
        startDate: criteria.startDate,
        endDate: criteria.endDate,
      });
    }, [fetchEvents, isSearchMode, messageSearchQuery, performSearch, searchEndDate, searchQuery, searchStartDate]);

    const searchToolbarChips = useMemo(
      () =>
        buildToolbarFilterChips(
          [
            {
              key: 'eventQuery',
              label: 'イベント',
              value: searchQuery,
              onRemove: () => applyToolbarSearchFilters({ eventQuery: '' }),
            },
            {
              key: 'messageQuery',
              label: '本文',
              value: messageSearchQuery,
              onRemove: () => applyToolbarSearchFilters({ messageQuery: '' }),
            },
            {
              key: 'startDate',
              label: '開始日',
              value: formatSearchChipDate(searchStartDate),
              onRemove: () => applyToolbarSearchFilters({ startDate: '' }),
            },
            {
              key: 'endDate',
              label: '終了日',
              value: formatSearchChipDate(searchEndDate),
              onRemove: () => applyToolbarSearchFilters({ endDate: '' }),
            },
          ],
          { onClearAll: clearSearch }
        ),
      [applyToolbarSearchFilters, clearSearch, messageSearchQuery, searchEndDate, searchQuery, searchStartDate]
    );

    const applyTargetSearchModal = useCallback(async () => {
      await applyEventTargetSearchDraft({
        searchDraft,
        setSearchQuery,
        setMessageSearchQuery,
        setSearchStartDate,
        setSearchEndDate,
        performSearch,
        setShowSearchModal,
        setErrorMessage: setSearchError,
      });
    }, [performSearch, searchDraft]);

    const requestRefreshTop = useCallback(() => {
      scheduleEventListRefreshTop({
        isSearchMode,
        orderFrozen,
        isBootLoading,
        refreshTopTimerRef,
        fetchEvents,
        delayMs: 120,
      });
    }, [isSearchMode, orderFrozen, isBootLoading, fetchEvents]);

    const handleLoadMoreEvents = useCallback(async ({ force = false, reason = 'default' } = {}) => {
      if (eventListLoadMorePendingRef.current && !isLoadingEvents) {
        eventListLoadMorePendingRef.current = false;
      }
      if (eventListLoadMorePendingRef.current) return;
      eventListLoadMorePendingRef.current = true;
      try {
        const canLoadByState = shouldLoadMoreEventList({
          isSearchMode,
          isBootLoading,
          isLoadingEvents,
          hasMoreEvents,
        });
        const canLoadMore = force
          ? !isSearchMode && !isBootLoading && !isLoadingEvents
          : canLoadByState;
        const loadedCount = await loadMoreEventList({
          canLoadMore,
          fetchEvents,
          pageLimit: EVENTS_PAGE_LIMIT,
          setHasMoreEvents,
        });
        if (loadedCount >= EVENTS_PAGE_LIMIT) {
          eventListForceFallbackExhaustedRef.current = false;
        } else if (force) {
          eventListForceFallbackExhaustedRef.current = true;
        }
      } finally {
        eventListLoadMorePendingRef.current = false;
      }
    }, [
      fetchEvents,
      hasMoreEvents,
      isBootLoading,
      isLoadingEvents,
      isSearchMode,
    ]);

    const handleEventListScroll = useCallback((event) => {
      const node = event?.currentTarget || eventListNode;
      const remaining = node
        ? node.scrollHeight - node.clientHeight - node.scrollTop
        : Number.NaN;
      // 検索モードは専用のページング（50件ずつ・過去分を追加）で下端到達時に追加取得する
      if (isSearchMode) {
        if (Number.isFinite(remaining) && remaining <= 200 && searchHasMore && !isSearchLoadingMore && !isSearching) {
          loadMoreSearchResults();
        }
        return;
      }
      const canLoadByState = shouldLoadMoreEventList({
        isSearchMode,
        isBootLoading,
        isLoadingEvents,
        hasMoreEvents,
      });
      const shouldForceFallback =
        remaining <= 180 &&
        !canLoadByState &&
        !isSearchMode &&
        !isBootLoading &&
        !isLoadingEvents &&
        !eventListForceFallbackExhaustedRef.current &&
        events.length >= EVENTS_PAGE_LIMIT;
      if (shouldForceFallback) {
        handleLoadMoreEvents({ force: true, reason: 'scroll-bottom-fallback' });
        return;
      }
      triggerEventListLoadMoreIfNeeded({
        container: event?.currentTarget || eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        thresholdPx: 180,
      });
    }, [
      eventListNode,
      events.length,
      handleLoadMoreEvents,
      hasMoreEvents,
      isBootLoading,
      isLoadingEvents,
      isSearchMode,
      searchHasMore,
      isSearchLoadingMore,
      isSearching,
      loadMoreSearchResults,
    ]);

    useEffect(() => {
      return observeEventListLoadMoreSentinel({
        sentinel: loadMoreNode,
        root: eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        rootMargin: '200px 0px',
        threshold: 0,
      });
    }, [eventListNode, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isSearchMode, loadMoreNode]);

    useEffect(() => {
      return observeEventListLoadMoreScroll({
        container: eventListNode,
        shouldLoad: () =>
          shouldLoadMoreEventList({
            isSearchMode,
            isBootLoading,
            isLoadingEvents,
            hasMoreEvents,
          }),
        onLoadMore: handleLoadMoreEvents,
        thresholdPx: 180,
      });
    }, [eventListNode, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isSearchMode]);

    useEffect(() => {
      if (!eventListNode || isSearchMode) return undefined;
      const rafId =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame(() => {
              triggerEventListLoadMoreIfNeeded({
                container: eventListNode,
                shouldLoad: () =>
                  shouldLoadMoreEventList({
                    isSearchMode,
                    isBootLoading,
                    isLoadingEvents,
                    hasMoreEvents,
                  }),
                onLoadMore: handleLoadMoreEvents,
                thresholdPx: 180,
              });
            })
          : null;
      return () => {
        if (rafId != null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(rafId);
        }
      };
    }, [eventListNode, events.length, handleLoadMoreEvents, hasMoreEvents, isBootLoading, isLoadingEvents, isSearchMode]);

    useEffect(() => {
      if (!eventUnreadMapArtist) return;

      setEvents(prev => {
        let changed = false;
        const next = prev.map(e => {
          const newBadge = Number(eventUnreadMapArtist[e.event_uuid] || 0);
          if (newBadge !== e.unreadCount) {
            changed = true;
            return { ...e, unreadCount: newBadge };
          }
          return e;
        });
        return changed ? next : prev;
      });

      const prevMap = prevUnreadMapRef.current || {};
      let needRefresh = false;
      for (const [eid, cntAny] of Object.entries(eventUnreadMapArtist)) {
        const cnt = Number(cntAny || 0);
        const prevCnt = Number(prevMap[eid] || 0);
        if (cnt > 0 && !displayedIdsRef.current.has(eid)) { needRefresh = true; break; }
        if (cnt > prevCnt) { needRefresh = true; break; }
      }
      prevUnreadMapRef.current = eventUnreadMapArtist;
      if (needRefresh) requestRefreshTop();
    }, [eventUnreadMapArtist, requestRefreshTop]);
  return {
    searchSummaryLabel,
    openTargetSearchModal,
    clearSearch,
    searchToolbarChips,
    applyTargetSearchModal,
    requestRefreshTop,
    handleEventListScroll,
  };
}